import { useEffect, useId, useState } from "react";
import { useI18n, type MessageKey } from "../i18n";
import { genrePath } from "../routes";
import { TitleGrid, usePagedTitles } from "../shelf";
import { usePortal } from "../state";
import { TITLE_SORTS, type TitleSort } from "../types";
import { EmptyState, Link, Notice, PageTitle } from "../ui";
import { FilmIcon } from "../icons";

/**
 * One genre's titles (`/genre/:id`), or every title (`/all`), in a grid that
 * loads the next page as its end nears, with "Sortera efter" on top. The
 * sort lives in the address bar (`?sort=`, replaced), so back and refresh
 * keep it; "Mest populära" is the default and is left out of the URL.
 *
 * The layout stays mounted across a sort change - only the grid empties and
 * refills - so a keyboard user's focus stays on the select.
 */
export function GenreScreen(props: {
  genreId: string | null;
  sort: TitleSort | null;
}) {
  const { genreId } = props;
  const sort = props.sort ?? "popular";
  const { t } = useI18n();
  const { replace } = usePortal();
  const selectId = useId();
  const paged = usePagedTitles(
    genreId === null ? { sort } : { genre: genreId, sort }
  );
  const [label, setLabel] = useState<string | null>(
    genreId === null ? t("store.allTitles") : null
  );
  useEffect(() => {
    if (genreId === null) setLabel(t("store.allTitles"));
    else if (paged.genre) setLabel(paged.genre.label);
  }, [genreId, paged.genre, t]);

  if (genreId !== null && paged.page && paged.genre === null) {
    return (
      <section className="panel">
        <PageTitle>{t("genre.notFound")}</PageTitle>
        <Link to="/">{t("return.browse")}</Link>
      </section>
    );
  }

  const total = paged.page?.pagination.total;
  return (
    <>
      <PageTitle>{label ?? t("common.loading")}</PageTitle>
      <div className="sortbar">
        <label htmlFor={selectId}>{t("genre.sortBy")}</label>
        <select
          id={selectId}
          value={sort}
          onChange={(e) =>
            replace(genrePath(genreId, e.target.value as TitleSort))
          }
        >
          {TITLE_SORTS.map((s) => (
            <option key={s} value={s}>
              {t(`genre.sort.${s}` as MessageKey)}
            </option>
          ))}
        </select>
        {total !== undefined && (
          <span className="muted" aria-live="polite">
            {t("genre.count", { count: total })}
          </span>
        )}
      </div>
      {paged.page?.unavailable && (
        <Notice kind="error">{t("browse.unavailable")}</Notice>
      )}
      {paged.error && (
        <div>
          <Notice kind="error">
            {t(
              paged.error === "catalogue_unavailable"
                ? "browse.unavailable"
                : "common.error"
            )}
          </Notice>
          <button type="button" className="btn" onClick={paged.reload}>
            {t("common.retry")}
          </button>
        </div>
      )}
      {paged.page && total === 0 && !paged.error && (
        <EmptyState icon={<FilmIcon />} title={t("store.empty.title")}>
          <p>
            <Link to="/">{t("return.browse")}</Link>
          </p>
        </EmptyState>
      )}
      <TitleGrid paged={paged} />
    </>
  );
}
